import './styles.scss'
import React, { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { DateTime } from 'luxon'

import settingsActions from 'redux/settings/actions'
import {
  searchUsers, setColumnsView, setUsersSearchChecks, updateUsersSearchFilters,
  updateUserSearchOrder, toggleSearchUsersIsAllChecks
} from 'redux/actions/cv_search'
import { addUserTab } from 'redux/actions/tabs'
import { addConversation } from 'redux/actions/conversations/conversations'
import { poolsActions } from 'redux/actions'
import SearchResult from 'components/SearchResult'
// import DownloadUsers from 'components/DownloadUsers'
import { MnemonicsBadge } from 'components/mnemonics'
import {
  actionOptions, colsShow, getFoldersOptions, getOperatorsOptions, getTagsOptions
} from 'constants/conversationSearch'
import { getChannelName } from 'utils/channelsUtils'
import { languages } from 'utils/languages'
import { lang } from 'utils/helpers'
import { massiveActionAPI } from 'api'
import SaveToastNotification from 'utils/toasts/SaveToastNotification'
import { USER_STATES } from '../../../constants'
import TimeAndDate from 'utils/TimeAndDate'

const SearchResultsView = () => {
  const dispatch = useDispatch()
  const { pathname } = useLocation()
  const { brands, delegations, cv_search, pools } = useSelector(store => store)
  const {
    cols_order: colsOrder, search_users, checked,
    isAllChecked, filters, search_users_count
  } = cv_search
  const [results, setResults] = useState([])
  const [resultActionOptions, setResultActionOptions] = useState({})
  const [loadingMassiveAction, setLoadingMassiveAction] = useState(false)
  const [resetMassiveAction, setResetMassiveAction] = useState(0)

  useEffect(() => {
    if (pathname === '/dashboard/conversations/search') dispatch(searchUsers())
  }, [pathname, dispatch])

  useEffect(() => {
    if (pools.refreshCounter) dispatch(searchUsers())
    return () => dispatch(poolsActions.resetRefreshCounter())
  }, [pools.refreshCounter])

  useEffect(() => {
    if (search_users?.length) {
      setResults(search_users.map(user => ({
        ...user,
        channel: lang(`channels_${getChannelName(user.channels)}`),
        pendingMsgCount: <MnemonicsBadge value={user.pendingMsgCount} location='search' />,
        createdAt: DateTime.fromISO(user.createdAt).toFormat('yyyy-LL-dd HH:mm'),
        updatedAt: DateTime.fromISO(user.updatedAt).toFormat('yyyy-LL-dd HH:mm'),
        lastMessageDate: user.lastMessageDate ? DateTime.fromISO(user.lastMessageDate).toFormat('yyyy-LL-dd HH:mm') : '',
        locale: getLanguageTranslation(user.locale === 'und' ? '' : user.locale === 'es_ES' ? 'es' : user.locale),
        state: lang(`states_${user.state}`),
        labels: user.categories?.map(({ name }) => name)?.join(', '),
        operators: user.operators?.map(({ email }) => email)?.join(', '),
        brand: brands[user.brands]?.name,
        delegation: delegations[user.delegations]?.name
      })))
    } else {
      setResults([])
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search_users])

  const getLanguageTranslation = (langType) => {
    const langData = languages.find(language => language.value.toLowerCase() === langType.toLowerCase())
    if (langData) return lang(langData.label || '')
    return langType
  }

  const formatFields = () => {
    const newColsShow = []
    colsOrder.forEach(colType => {
      newColsShow.push({ ...colsShow.find(col => col.value === colType), isSelected: true })
    })
    colsShow.forEach(col => {
      if (!newColsShow.find(newCol => newCol.value === col.value)) newColsShow.push({ ...col, isSelected: false })
    })
    return newColsShow
  }

  const onChangeConfigHeader = newColsOrder => {
    dispatch(setColumnsView(newColsOrder))
    dispatch(settingsActions.set({ conversation: { searchUserCols: newColsOrder } }))
  }

  const toggleOrder = (field) => {
    dispatch(updateUserSearchOrder({ sortField: field }))
  }

  const onChange = (field, value) => {
    clearSelection()
    dispatch(updateUsersSearchFilters(field, value))
  }

  const selectUser = (conversation) => {
    const channel = getChannelName(conversation.channels)
    dispatch(addConversation(conversation))
    dispatch(addUserTab(conversation.id, channel))
  }

  const onChangeActionResultCheck = (check, conversationId, isAllOnPage, isAll) => {
    if (isAll || (!check && isAllChecked)) dispatch(toggleSearchUsersIsAllChecks())

    let tempCheck = checked
    if (!isAllOnPage) {
      if (check) {
        tempCheck.push(conversationId)
      } else {
        const index = tempCheck.indexOf(conversationId)
        tempCheck.splice(index, 1)
      }
    } else {
      if (!check) {
        tempCheck = []
      } else {
        for (const { _id } of search_users) {
          if (!checked.includes(_id)) tempCheck.push(_id)
        }
      }
    }
    dispatch(setUsersSearchChecks(tempCheck))
  }

  const clearSelection = () => {
    if (checked?.length) dispatch(setUsersSearchChecks([]))
    if (isAllChecked) dispatch(toggleSearchUsersIsAllChecks())
  }

  const onChangeAction = async ({ action, brands, delegations }) => {
    if (!action || resultActionOptions[action]?.length) return

    if (['assign_operators', 'delete_assigned_operators'].includes(action)) {
      setResultActionOptions(prevState => ({ ...prevState, loading: true }))
      getOperatorsOptions({ brands, delegations })
        .then(data => {
          setResultActionOptions(prevState => ({
            ...prevState,
            'assign_operators': data,
            'delete_assigned_operators': data,
            loading: false
          }))
        })
    } else if (action === 'change_status') {
      const states = USER_STATES['all'].map(state => ({
        value: state,
        label: lang(`states_${state}`)
      }))
      setResultActionOptions(prevState => ({
        ...prevState,
        'change_status': states
      }))
    } else if (['add_tags', 'remove_tags'].includes(action)) {
      setResultActionOptions(prevState => ({ ...prevState, loading: true }))
      getTagsOptions()
        .then(data => {
          setResultActionOptions(prevState => ({
            ...prevState,
            'add_tags': data,
            'remove_tags': data,
            loading: false
          }))
        })
    } else if (['add_to_folder', 'delete_from_folder'].includes(action)) {
      setResultActionOptions(prevState => ({ ...prevState, loading: true }))
      getFoldersOptions()
        .then(data => {
          setResultActionOptions(prevState => ({
            ...prevState,
            'add_to_folder': data,
            'delete_from_folder': data,
            loading: false
          }))
        })
    }
  }

  const onApplyMassAction = ({ action, resultAction, messageAction, historyRange }) => {
    const data = { action, all: isAllChecked }

    if (isAllChecked) {
      const {
        searchText, outbound, assigned, providers, brands, delegations,
        states, labels, folders, lastMessageDate, createdAt, updatedAt
      } = filters
      const massActionFilters = {}
      if (searchText) massActionFilters.text = searchText
      if (providers?.length) massActionFilters.providers = providers.map(({ value }) => value)
      if (brands?.length) massActionFilters.brands = brands.map(({ value }) => value)
      if (delegations?.length) massActionFilters.delegations = delegations.map(({ value }) => value)
      if (states?.length) massActionFilters.states = states.map(({ value }) => value)
      if (assigned?.length) massActionFilters.assigned = assigned.map(({ value }) => value)
      if (labels?.length) massActionFilters.categories = labels.map(({ value }) => value)
      if (folders?.length) massActionFilters.lists = folders.map(({ value }) => value)
      if (outbound) massActionFilters.outbound = outbound
      if (filters.createdAt?.[0] || filters.createdAt?.[1]) {
        massActionFilters.createdAt = TimeAndDate.betweenEndStartDayUTC(
          createdAt?.[0],
          createdAt?.[1]
        )
      }
      if (filters.updatedAt?.[0] || filters.updatedAt?.[1]) {
        massActionFilters.updatedAt = TimeAndDate.betweenEndStartDayUTC(
          updatedAt?.[0],
          updatedAt?.[1]
        )
      }
      if (filters.lastMessageDate?.[0] || filters.lastMessageDate?.[1]) {
        massActionFilters.lastMessageDate = TimeAndDate.betweenEndStartDayUTC(
          lastMessageDate?.[0],
          lastMessageDate?.[1]
        )
      }

      if (Object.keys(massActionFilters).length) data.filters = massActionFilters
    } else {
      data.conversations = checked
    }
    if ([
      'assign_operators',
      'delete_assigned_operators',
      'add_tags',
      'remove_tags',
      'add_to_folder',
      'delete_from_folder',
      'change_centers'
    ].includes(action)) data.newContentIds = resultAction
    if (action === 'add_internal_comment') data.messageInfo = messageAction
    if (action === 'change_status') data.newConversationStatus = resultAction
    if (['export_csv', 'export_csv_with_assigned'].includes(action)) {
      const colsToShowExportCSV = colsOrder.map(it => ({ value: it, label: `table_header_col_${it}_conversations` }))
      data.colsToShowExportCSV = colsToShowExportCSV
      data.timezone = DateTime.local().zoneName

      if (historyRange) data.historyRange = historyRange
    }

    setLoadingMassiveAction(true)
    massiveActionAPI.createMassiveAction(data)
      .then(() => {
        clearSelection()
        setResetMassiveAction(prevState => prevState + 1)
        SaveToastNotification.ok({ text: lang('dashboard_massive-action-progress-notified-finished') })
      })
      .catch(() => {
        SaveToastNotification.error({ text: lang('dashboard_error-executing-massive-action-try-again') })
      })
      .finally(() => setLoadingMassiveAction(false))
  }

  return (
    <div className='search-results-view'>
      <SearchResult
        customClassNameContainer='search-results-view__results-container'
        originType='conversations'
        colsToShow={colsOrder}
        fieldsColumns={formatFields()}
        onChangeConfigHeader={onChangeConfigHeader}
        results={results}
        checkResult={checked}
        toggleOrder={toggleOrder}
        sortType={filters.sortType}
        sortByField={filters.sortField}
        onChangeMassiveAction={onChange}
        detailResult={selectUser}
        limit={filters.limit}
        totalResult={search_users_count}
        numberPage={filters.page}
        actionOptions={actionOptions}
        onChangeAction={action => {
          onChangeAction({ action, brands: Object.keys(brands), delegations: Object.keys(delegations) })
        }}
        resultActionOptions={resultActionOptions}
        onChangeActionResultCheck={onChangeActionResultCheck}
        isAllSelected={isAllChecked}
        toggleSelectedAll={() => onChangeActionResultCheck(!isAllChecked, null, true, true)}
        onApplyMassAction={onApplyMassAction}
        loadingMassiveAction={loadingMassiveAction}
        resetMassiveAction={resetMassiveAction}
      />
      {/* <DownloadUsers customClassNameContainer='search-results-view__download-btn' /> */}
    </div>
  )
}

export default SearchResultsView
